////jc
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import { baseUrl, uploadFile, mediaUrl } from "../../../Utils/FileUpload";
import {
  Submit,
  EditGeneralSettingTitle,
  GeneralSettingTitle,
} from "../../../Utils/Constants";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CKEditor } from "@ckeditor/ckeditor5-react"; // Import CKEditor 5
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"; // Import the CKEditor build
import { useNavigate, useLocation } from "react-router-dom";
import useCustomToasts from "../../../hooks/useCustomToast";

interface FormValues {
  phone: string;
  address: string;
  email: string;
  about: string;
  smtp_password: string;
  term_condition: string;
  customer_term_condition: string;
  logo: FileList | null;
  currency: string;
  is_video: string;
}
interface ErrorResponse {
  message: string;
}
export default function EditGeneralSetting() {
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const [loading, setLoading] = useState(true);
  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      phone: "",
      address: "",
      email: "",
      about: "",
      currency: "",
      is_video: "",
      smtp_password: "",
      term_condition: "",
      logo: null,
    },
  });
  const [existingFile, setExistingFile] = useState<string>("");
  const location = useLocation();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const togglePasswordVisibility = () => setPasswordVisible(!isPasswordVisible);
  const customEditorConfig = {
    toolbar: [
      "heading",
      "|",
      "bold",
      "italic",
      "link",
      "bulletedList",
      "numberedList",
      "blockQuote",
      "|",
      "insertTable",
      "undo",
      "redo",
    ],
  };
  useEffect(() => {
    const fetchStaffsDetails = async () => {
      try {
        const token = localStorage.getItem("token");

        const result = await axios.post(
          `${baseUrl}general_setting/viewbyid`,
          {
            id: location.state.id,
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log(result, "result");
        setValue("phone", result.data.phone);
        setValue("address", result.data.address);
        setValue("email", result.data.email);
        setValue("about", result.data.about);
        setValue("smtp_password", result.data.smtp_password);
        setValue("term_condition", result.data.term_condition);
        setValue(
          "customer_term_condition",
          result.data.customer_term_condition
        );
        setValue("logo", result.data.logo);
        setValue("currency", result.data.currency);
        setValue("is_video", result.data.is_video ? "1" : "0");
        setExistingFile(result.data.logo);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching staffs details:", error);
      }
    };
    fetchStaffsDetails();
  }, [setValue]);

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      let filename = existingFile;

      // Check if a new file is provided
      if (data.logo && data.logo instanceof FileList && data.logo.length > 0) {
        const file = data.logo?.[0];
        filename = await uploadFile(file);
        setExistingFile(filename);
      }

      const editdata = {
        phone: data.phone,
        email: data.email,
        address: data.address,
        about: data.about,
        currency: data.currency,
        smtp_password: data.smtp_password,
        term_condition: data.term_condition,
        customer_term_condition: data.customer_term_condition,
        is_video: data.is_video,
        logo: filename || existingFile,
        id: location.state.id,
      };
      // console.log(editdata, "editdata");
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${baseUrl}general_setting/update`,
        editdata,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        SuccessToast("Data updated successfully", "/view_general_setting");
      } else {
        ErrorToast("Failed to update");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorResponse = axiosError.response?.data as ErrorResponse;
      const errorMessage =
        errorResponse?.message || "An unexpected error occurred";
      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 2000);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  // Prevent form submission on Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={EditGeneralSettingTitle}
        parent={GeneralSettingTitle}
        title={EditGeneralSettingTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="g-3">
                <Col xs={12}>
                  <Label htmlFor="phone">Contact Number</Label>
                  <Controller
                    name="phone"
                    control={control}
                    rules={{
                      required: "Contact number is required",
                      pattern: {
                        value: /^[0-9]{7,15}$/,
                        message: "Invalid contact number",
                      },
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="text"
                          id="phone"
                          placeholder="Phone"
                          {...field}
                          invalid={!!errors.phone}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.phone ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.phone && (
                          <FormFeedback>{errors.phone.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="email">Email</Label>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email address",
                      },
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="email"
                          id="email"
                          placeholder="Email"
                          {...field}
                          invalid={!!errors.email}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.email ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.email && (
                          <FormFeedback>{errors.email.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Address</Label>
                  <Controller
                    name="address"
                    control={control}
                    rules={{ required: "Address is required" }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="textarea"
                          id="address"
                          placeholder="Address"
                          {...field}
                          invalid={!!errors.address}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.address ? "red" : "",
                          }}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.address && (
                          <FormFeedback>{errors.address.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label>Enable Video Call</Label>
                  <Controller
                    name="is_video"
                    control={control}
                    defaultValue="0"
                    rules={{ required: "Please select an option" }}
                    render={({ field }) => (
                      <div
                        style={{ display: "flex", gap: "20px" }}
                        className="card-wrapper border rounded-3 p-3"
                      >
                        <div>
                          <input
                            type="radio"
                            id="videoNo"
                            value="0"
                            checked={field.value === "0"}
                            onChange={field.onChange}
                          />
                          <label
                            htmlFor="videoNo"
                            style={{ marginLeft: "5px" }}
                          >
                            No
                          </label>
                        </div>
                        <div>
                          <input
                            type="radio"
                            id="videoYes"
                            value="1"
                            checked={field.value === "1"}
                            onChange={field.onChange}
                          />
                          <label
                            htmlFor="videoYes"
                            style={{ marginLeft: "5px" }}
                          >
                            Yes
                          </label>
                        </div>
                      </div>
                    )}
                  />
                  {errors.is_video && (
                    <p className="text-danger" style={{ fontSize: "12px" }}>
                      {errors.is_video.message}
                    </p>
                  )}
                </Col>

                <Col xs={12}>
                  <Label htmlFor="address">Currency Symbol</Label>
                  <Controller
                    name="currency"
                    control={control}
                    rules={{ required: "Currency is required" }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="text"
                          id="currency"
                          placeholder="Currency"
                          {...field}
                          invalid={!!errors.currency}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.currency ? "red" : "",
                          }}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.currency && (
                          <FormFeedback>{errors.currency.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="about-name">About</Label>
                  <Controller
                    name="about"
                    control={control}
                    rules={{
                      required: "About is required",
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          id="about-name"
                          type="text"
                          placeholder="About"
                          {...field}
                          invalid={!!errors.about}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.about ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.about && (
                          <FormFeedback>{errors.about.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="formFile">Logo</Label>
                  <Controller
                    name="logo"
                    control={control}
                    rules={{ required: "Logo is required" }}
                    render={({ field: { onChange, onBlur, ref } }) => (
                      <>
                        <Input
                          id="formImage"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              onChange(e.target.files);
                              // Update the image preview immediately
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setExistingFile(reader.result as string);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          onBlur={onBlur}
                          innerRef={ref}
                          onKeyDown={handleKeyDown}
                        />
                        {existingFile && (
                          <img
                            src={
                              existingFile.startsWith("data:")
                                ? existingFile
                                : `${mediaUrl}/${existingFile}`
                            }
                            alt="Preview"
                            width="70"
                            height="70"
                            style={{ marginTop: "5px", borderRadius: "10px" }}
                          />
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="password">Agent Terms & Condition</Label>
                  <Controller
                    name="term_condition"
                    control={control}
                    defaultValue=""
                    rules={{ required: "Agent terms & condition is required" }}
                    render={({ field }) => (
                      <div
                        style={{
                          border: errors.term_condition
                            ? "1px solid red"
                            : "1px solid #ced4da",
                          borderRadius: "4px",
                        }}
                      >
                        <CKEditor
                          editor={ClassicEditor}
                          config={customEditorConfig}
                          {...field}
                          data={field.value || ""}
                          onChange={(event, editor) => {
                            const data = editor.getData(); // Get HTML content
                            field.onChange(data); // Update form value with HTML
                          }}
                          onReady={(editor) => {
                            // Provide a focus method
                            field.ref({
                              focus: () => editor.editing.view.focus(),
                            });
                          }}
                        />
                      </div>
                    )}
                  />
                  {errors.term_condition && (
                    <p className="text-danger" style={{ fontSize: "12px" }}>
                      {errors.term_condition.message}
                    </p>
                  )}
                </Col>

                <Col xs={12}>
                  <Label htmlFor="password"> Customer Terms & Condition</Label>
                  <Controller
                    name="customer_term_condition"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: "Customer terms & condition is required",
                    }}
                    render={({ field }) => (
                      <div
                        style={{
                          border: errors.customer_term_condition
                            ? "1px solid red"
                            : "1px solid #ced4da",
                          borderRadius: "4px",
                        }}
                      >
                        <CKEditor
                          editor={ClassicEditor}
                          config={customEditorConfig}
                          {...field}
                          data={field.value || ""}
                          onChange={(event, editor) => {
                            const data = editor.getData(); // Get HTML content
                            field.onChange(data); // Update form value with HTML
                          }}
                          onReady={(editor) => {
                            // Provide a focus method
                            field.ref({
                              focus: () => editor.editing.view.focus(),
                            });
                          }}
                        />
                      </div>
                    )}
                  />
                  {errors.customer_term_condition && (
                    <p className="text-danger" style={{ fontSize: "12px" }}>
                      {errors.customer_term_condition.message}
                    </p>
                  )}
                </Col>

                <Col xs={12}>
                  <Label htmlFor="password">Password</Label>
                  <Controller
                    name="smtp_password"
                    control={control}
                    rules={{
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters",
                      },
                      pattern: {
                        value: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{6,}$/,
                        message: "Password must contain letters and numbers",
                      },
                    }}
                    render={({ field }) => (
                      <div style={{ position: "relative" }}>
                        <Input
                          type={isPasswordVisible ? "text" : "password"}
                          id="password"
                          placeholder="Enter password"
                          {...field}
                          invalid={!!errors.smtp_password}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.smtp_password ? "red" : "",
                          }}
                          autoComplete="new-password"
                        />
                        <div
                          onClick={togglePasswordVisibility}
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            color: "#888",
                          }}
                        >
                          {isPasswordVisible ? "Hide" : "Show"}
                        </div>
                        {errors.smtp_password && (
                          <FormFeedback>
                            {errors.smtp_password.message}
                          </FormFeedback>
                        )}
                      </div>
                    )}
                  />
                </Col>

                <Col xs={12} className="mt-2">
                  <Btn color="primary" disabled={isButtonDisabled}>
                    {Submit}
                  </Btn>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer
        toastStyle={{
          userSelect: "none",
        }}
      />
    </>
  );
}
